import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { fetcher } from 'shared/api/http-client'
import { NOEMBED_BASE_URL } from 'shared/constants'
import type { UseQueryClearOptions } from 'shared/types/tanstack-utility-types'
import {
  isEmbeddedVideoUrlTooLong,
  isValidEmbeddedVideoUrl,
} from 'modules/attachments/utils/attachment-utils'
import { attachmentKeys } from './key-factory'

interface OEmbedInterface {
  title: string
  thumbnail_url: string
  playback: {
    type: 'iframe'
    src: string
  }
}

const VIDEO_PLAYER_URL_PATTERN =
  /^https:\/\/(?:www\.youtube(?:-nocookie)?\.com\/embed\/[\w-]{11}|www\.facebook\.com\/plugins\/video\.php|clips\.twitch\.tv\/embed|player\.vimeo\.com\/video\/\d+|fast\.wistia\.(?:com|net)\/embed\/iframe\/[\da-z]+|www\.loom\.com\/embed\/[\da-f]{32})(?:[#?].*)?$/i

const getEmbeddedVideo = async (url: string, signal?: AbortSignal): Promise<OEmbedInterface> => {
  if (!isValidEmbeddedVideoUrl(url) || isEmbeddedVideoUrlTooLong(url)) {
    throw new Error('unsupported video URL')
  }

  const metadata = await fetcher(`${NOEMBED_BASE_URL}?url=${encodeURIComponent(url)}`, {
    signal,
    timeout: 15_000,
  })

  const result = z
    .union([
      z.object({ error: z.string() }),
      z.object({
        error: z.never().optional(),
        title: z.string().catch(url),
        thumbnail_url: z
          .url({ protocol: /^https$/ })
          .startsWith('https://')
          .max(1000),
        playback: z.object({
          type: z.literal('iframe'),
          src: z.string().regex(VIDEO_PLAYER_URL_PATTERN),
        }),
      }),
    ])
    .safeParse(metadata)

  if (!result.success) throw new Error('video metadata is unavailable')
  if ('error' in result.data) throw new Error(result.data.error ?? 'video metadata is unavailable')

  const thumbnail = new URL(result.data.thumbnail_url)
  if (thumbnail.username || thumbnail.password || thumbnail.port)
    throw new Error('video thumbnail is unavailable')

  return {
    title: result.data.title,
    thumbnail_url: result.data.thumbnail_url,
    playback: result.data.playback,
  }
}

interface UseEmbeddedVideoOptions extends UseQueryClearOptions<
  OEmbedInterface,
  Error,
  OEmbedInterface
> {
  url: string
}

export const useEmbeddedVideo = ({
  url,
  ...options
}: UseEmbeddedVideoOptions): UseQueryResult<OEmbedInterface> =>
  useQuery({
    queryKey: attachmentKeys.embeddedVideo(url),
    queryFn: async ({ signal }) => getEmbeddedVideo(url, signal),
    staleTime: 30_000,
    retry: false,
    ...options,
  })
